import { useFetchSingleVehicle } from '@@partner:graphql/query/vehicle/singleVehicle.gql'
import AppLoader from '@components/common/AppLoader'
import CommonErrorDisplay from '@components/common/CommonErrorDisplay'
import LogisticTopMenu from '@components/logistic/LogisticTopMenu'
import VehiclesSubMenu from '@components/logistic/vehicles/VehiclesSubMenu'
import React, { useCallback, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Button, Checkbox, Col, Form, Grid, Heading, Input, Panel, Row, Tabs } from 'rsuite'

import './SingleVehiclePage.scss'
import VehicleShiftsList from '@components/logistic/vehicles/shift/VehicleShiftsList'
import { VehicleBox, VehicleInfo } from '@@partner:types:app'
import { mergeLeft } from 'rambda'
import { useMutateUpdateVehicleInfo } from '@@partner:graphql/mutation/vehicle/updateVehicle.gql'
import { makeGraphQLErrorNotification, notifyOk } from '@@partner:helpers/notification'
import LastActiveVehicleShift from '@components/logistic/vehicles/shift/LastActiveVehicleShift'
import LastCompletedVehicleShift from '@components/logistic/vehicles/shift/LastCompletedVehicleShift'
import VehicleIncidentsList from '@components/logistic/vehicles/incident/VehicleIncidentsList'
import ShopBoxes from '@components/shop/form/ShopBoxes'
import VehicleBoxes from './VehicleBoxes'

type VehicleInfoInput = Pick<VehicleInfo, 'year' | 'vehicle_type_id' | 'license_plate' | 'is_active' | 'color'>
type VehicleAdditionalInfoInput = Pick<VehicleInfo, 'capacity_kg' | 'capacity_m3' | 'temp_min_c' | 'temp_max_c' | 'range_km' | 'average_speed'>

export default function SingleVehiclePage () {
  const { vehicleId, brandSlug } = useParams<any>()

  const [ editInfo, setEditInfo ] = useState(false)
  const [ editAdditionalInfo, setEditAdditionalInfo ] = useState(false)
  const [ editComment, setEditComment ] = useState(false)

  const [ newComment, setNewComment ] = useState<string>('')
  const [ vehicleInfo, setVehicleInfo ] = useState<VehicleInfoInput | null>(null)
  const [ additionalInfo, setAdditionalInfo ] = useState<VehicleAdditionalInfoInput | null>(null)
  const [ boxes, setBoxes ] = useState<Omit<VehicleBox, 'id'>[]>([])

  const { vehicle, loading, error } = useFetchSingleVehicle(vehicleId)

  useEffect(() => {
    if (!loading && vehicle) {
      setVehicleInfo({
        license_plate: vehicle.license_plate,
        is_active: vehicle.is_active,
        color: vehicle.color,
        vehicle_type_id: vehicle.vehicle_type_id,
        year: vehicle.year
      })
      setNewComment(vehicle.comment)
      setAdditionalInfo({
        capacity_kg: vehicle.capacity_kg,
        capacity_m3: vehicle.capacity_m3,
        temp_min_c: vehicle.temp_min_c,
        temp_max_c: vehicle.temp_max_c,
        range_km: vehicle.range_km,
        average_speed: vehicle.average_speed
      })
      setBoxes(vehicle.boxes || [])
    }
  }, [ loading, vehicle ])

  const [ updateVehicle ] = useMutateUpdateVehicleInfo()

  const handleSaveVehicle = async () => {
    if (vehicleInfo) {
      await updateVehicle({
        variables: {
          newData: {
            id: vehicleId,
            ...vehicleInfo
          },
          upsert: false
        }
      })
        .then(({ data }) => {
          notifyOk('Данные сохранены')
        })
        .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
    }
  }

  const handleFormChange = useCallback(
    (formData: any) => {
      const newData = { ...additionalInfo, ...formData }
      setAdditionalInfo(newData)
    },
    [ setAdditionalInfo, additionalInfo ]
  )

  const handleSaveAdditionalInfo = async () => {
    if (additionalInfo) {
      await updateVehicle({
        variables: {
          newData: {
            id: vehicleId,
            vehicle_type_id: vehicle?.vehicle_type_id,
            ...additionalInfo
          },
          upsert: false
        }
      })
        .then(({ data }) => {
          notifyOk('Данные сохранены')
        })
        .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
    }
  }

  const handleSaveComment = async () => {
    if (vehicleInfo) {
      await updateVehicle({
        variables: {
          newData: {
            id: vehicleId,
            vehicle_type_id: vehicle?.vehicle_type_id,
            comment: newComment
          },
          upsert: false
        }
      })
        .then(({ data }) => {
          notifyOk('Данные сохранены')
          setEditComment(false)
        })
        .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
    }
  }

  const handleSaveBoxes = async () => {
    if (vehicleInfo) {
      await updateVehicle({
        variables: {
          newData: {
            id: vehicleId,
            vehicle_type_id: vehicle?.vehicle_type_id,
            boxes
          },
          upsert: false
        }
      })
        .then(({ data }) => {
          notifyOk('Ячейки сохранены')
          setEditComment(false)
        })
        .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
    }
  }

  const preparedVehicleModel = [ vehicle?.brand, vehicle?.model, vehicle?.year ].filter(Boolean).join(' ')

  if (loading) {
    return <AppLoader content='Загружаем информацию о транспорте' />
  }

  if (error || !vehicle) {
    return <CommonErrorDisplay error={error} title={'Ошибка при загрузке транспорта'} />
  }

  return (
    <div className='SingleVehiclePage'>
      <h1 className="u-heading">Информация о транспорте { vehicle && '(' + vehicle.license_plate + ')' }</h1>

      <LogisticTopMenu />
      <VehiclesSubMenu />

      <Grid fluid>
        <Col xs={24} sm={24} md={24} lg={12} xxl={6}>
          <Panel
            header={`${vehicle.license_plate} - ${preparedVehicleModel}`}
            shaded
            className='my-5'
          >
            <Row>
              <Col xs={24}>
                { editInfo && vehicleInfo ? (
                  <div>
                    <div>
                      <strong>Номер </strong>
                      <Input
                        value={vehicleInfo?.license_plate}
                        onChange={value => setVehicleInfo(mergeLeft({ license_plate: value }, vehicleInfo))}
                      />
                    </div>
                    <div>
                      <strong>Активен </strong>
                      <Checkbox
                        checked={vehicleInfo?.is_active}
                        onChange={value => setVehicleInfo(mergeLeft({ is_active: value }, vehicleInfo))}
                      />
                    </div>
                    <div>
                      <strong>Цвет </strong>
                      <Input
                        value={vehicleInfo?.color}
                        onChange={value => setVehicleInfo(mergeLeft({ color: value }, vehicleInfo))}
                      />
                    </div>
                  </div>
                ) : (
                  <div className='SingleVehiclePage__Info'>
                    <h2 style={{
                      color: vehicle.is_active ? 'green' : 'red'
                    }}>
                      { vehicle.is_active ? 'Активен' : 'Отключен' }
                    </h2>
                    <div>
                      <strong>Марка </strong>
                      { vehicle.brand }
                    </div>
                    <div>
                      <strong>Модель </strong>
                      { vehicle.model }
                    </div>
                    <div>
                      <strong>Год выпуска </strong>
                      { vehicle.year }
                    </div>
                    <div>
                      <strong>Номер </strong>
                      { vehicle.license_plate }
                    </div>
                    <div>
                      <strong>VIN номер </strong>
                      { vehicle.vin }
                    </div>
                    <div>
                      <strong>Цвет</strong>
                      { vehicle.color }
                    </div>

                  </div>
                )}
              </Col>
            </Row>
            <Row className='my-3 justify-end flex'>
              { editInfo && (
                <Button onClick={handleSaveVehicle} appearance='primary' color='green'>
                  Сохранить
                </Button>
              )}
              <Button onClick={() => setEditInfo(!editInfo)} appearance='primary' color={editInfo ? undefined : 'blue'}>
                { editInfo ? 'Отменить' : 'Изменить' }
              </Button>
            </Row>
          </Panel>
          <Panel
            header='Параметры транспорта'
            shaded
            className='my-5'
          >
            <Row className='my-5'>
              <Col xs={24}>
                { editAdditionalInfo ? (
                  <Form
                    className='SingleVehiclePage__AdditionalForm'
                    formValue={additionalInfo || {}}
                    onChange={handleFormChange}
                    onSubmit={handleSaveAdditionalInfo}
                  >
                    <Form.Group>
                      <Form.ControlLabel>Грузоподъёмность</Form.ControlLabel>
                      <Form.Control
                        name='capacity_kg'
                        type='number'
                        min={0}
                        step={10}
                        
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.ControlLabel>Объем</Form.ControlLabel>
                      <Form.Control
                        name='capacity_m3'
                        type='number'
                        min={0}
                        step={0.1}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.ControlLabel>Минимальная температура</Form.ControlLabel>
                      <Form.Control
                        name='temp_min_c'
                        type='number'
                        min={-50}
                        max={50}
                        step={1}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.ControlLabel>Максимальная температура</Form.ControlLabel>
                      <Form.Control
                        name='temp_max_c'
                        type='number'
                        min={-50}
                        max={150}
                        step={1}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.ControlLabel>Средняя скорость</Form.ControlLabel>
                      <Form.Control
                        name='average_speed'
                        type='number'
                        min={0}
                        step={1}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.ControlLabel>Максимальная дистанция</Form.ControlLabel>
                      <Form.Control
                        name='range_km'
                        type='number'
                        min={0}
                        step={1}
                      />
                    </Form.Group>
                  </Form>
                ) : (
                  <div className='SingleVehiclePage__Info'>
                    <div>
                      <strong>Грузоподъёмность </strong>
                      { vehicle.capacity_kg } кг
                    </div>
                    <div>
                      <strong>Объем </strong>
                      { vehicle.capacity_m3 } м³
                    </div>
                    <div>
                      <strong>Минимальная температура </strong>
                      { vehicle.temp_min_c } °C
                    </div>
                    <div>
                      <strong>Максимальная температура </strong>
                      { vehicle.temp_max_c } °C
                    </div>
                    <div>
                      <strong>Максимальная дистанция </strong>
                      { vehicle.range_km } км
                    </div>
                    <div>
                      <strong>Средняя скорость </strong>
                      { vehicle.average_speed } км/ч
                    </div>
                  </div>
                )}
              </Col>
            </Row>
            <Row className='my-3 justify-end flex'>
              { editAdditionalInfo && (
                <Button 
                  onClick={handleSaveAdditionalInfo} 
                  appearance='primary' 
                  color='green'
                  className='mr-2'
                >
                  Сохранить
                </Button>
              )}
              <Button onClick={() => setEditAdditionalInfo(!editAdditionalInfo)} appearance='primary' color={editAdditionalInfo ? undefined : 'blue'}>
                { editAdditionalInfo ? 'Отменить' : 'Изменить' }
              </Button>
            </Row>
          </Panel>
          <Panel
            header='Комментарий'
            shaded
            className='my-5'
          >
            <div className='SingleVehiclePage__Comment'>
              { editComment ? (
                <Input
                  value={newComment}
                  onChange={(e) => setNewComment(e)}
                />
              ) : (
                <span onClick={() => setEditComment(true)}>
                  { vehicle.comment || 'Нет комментария' }
                </span>
              )}
            </div>
            { editComment ? (
              <div className='flex justify-end gap-3 my-3'>
                <Button onClick={handleSaveComment} appearance='primary' color='green'>
                  Сохранить
                </Button>
                <Button onClick={() => setEditComment(false)} appearance='primary'>
                  Отменить
                </Button>
              </div>
            ) : (
              <div className='flex justify-end gap-3 my-3'>
                <Button onClick={() => setEditComment(true)} appearance='primary' color='green'>
                  Изменить
                </Button>
              </div>
            )}
          </Panel>
        </Col>
        <Col xs={24} sm={24} md={24} lg={12} xxl={18}>
          <Tabs appearance='pills' className='my-5' defaultActiveKey={'1'}>
            <Tabs.Tab title='Смены' eventKey='1'>
              <Row>
                <Panel
                  header='Последняя активная смена'
                  shaded
                  className='mb-5'
                >
                  <LastActiveVehicleShift vehicleId={vehicleId} />
                </Panel>
              </Row>
              <Row>
                <Panel
                  header='Последняя завершенная смена'
                  shaded
                  className='mb-5'
                >
                  <LastCompletedVehicleShift vehicleId={vehicleId} />
                </Panel>
              </Row>
              <Row>
                <Panel
                  header='Смены'
                  shaded
                  className='mb-5'
                >
                  <VehicleShiftsList vehicleId={vehicleId} />
                </Panel>
              </Row>
            </Tabs.Tab>
            <Tabs.Tab title='Инциденты' eventKey='2'>
              <Row>
                <Panel
                  header='Инциденты'
                  shaded
                  className='my-5'
                >
                  <VehicleIncidentsList vehicleId={vehicleId} />
                </Panel>
              </Row>
            </Tabs.Tab>
            <Tabs.Tab title='Ячейки' eventKey='3'>
              <Row>
                <Col xs={24}>
                  <Panel
                    header='Ячейки'
                    shaded
                    className='my-5'
                  >
                    <VehicleBoxes
                      vehicleId={vehicleId}
                      boxes={boxes || []}
                      onChange={setBoxes}
                    />
                    <Button
                      appearance='primary'
                      onClick={handleSaveBoxes}
                      color='green'
                      className='mr-2'
                    >
                      Сохранить
                    </Button>
                  </Panel>
                </Col>
              </Row>
            </Tabs.Tab>
          </Tabs>
        </Col>
      </Grid>
    </div>
  )
}